import {
  Button,
  Menu,
  MenuTrigger,
  MenuTriggerChildProps,
  MenuPopover,
  MenuList,
  MenuItem,
  Tooltip,
} from '@fluentui/react-components';
import { MoreHorizontalRegular, Open24Regular } from '@fluentui/react-icons';
import { Course as CourseTeamsServicesType } from '@mnspro-cloud/teams-services';
import cn from 'classnames';
import { get } from 'lodash';
import React, { KeyboardEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyAvatarGroup } from '../Avatar';
import { Text } from '../Text/Text';
import { replaceClassNamePlaceholder } from '../Classbook';
import { ThemeMode, useProviderTheme } from '../hooks';
import { getCourseBackgroundColor, getCourseColor, hexToRgbArray } from '../Utils';
import { CourseTileData } from './CourseTile.interface';
import './CourseTile.scss';

export interface CourseTileProps {
  course: CourseTileData | CourseTeamsServicesType;
  /**
   * Primary click handler. When omitted, the tile is inert (not focusable, no
   * click). Teams omits this and passes {@link onOpenInBrowser} instead, so
   * clicking a tile never springboards the user out of Teams.
   */
  onClick?: (courseId: string) => void;
  /**
   * When set, the tile renders a ⋯-menu with an "open in browser" action.
   * Teams passes an `app.openLink`-based handler here.
   */
  onOpenInBrowser?: (courseId: string) => void;
}

export const CourseTile: React.FC<CourseTileProps> = ({ course, onClick, onOpenInBrowser }) => {
  const { t } = useTranslation();
  const { mode } = useProviderTheme();
  const isClickable = Boolean(onClick);

  const classNames = course.classNames
    .map((className) => replaceClassNamePlaceholder(className, t))
    .join(', ');

  const backgroundColor = useMemo(() => {
    if (mode === ThemeMode.HighContrast) {
      return undefined;
    }

    const opacity = mode === ThemeMode.Dark ? '0.4' : '1';
    return getCourseBackgroundColor(course, opacity);
  }, [course, mode]);

  const borderColor = useMemo(() => {
    if (mode === ThemeMode.HighContrast) {
      return undefined;
    }

    const courseColorHex = getCourseColor(course);
    const rgbArray = hexToRgbArray(courseColorHex);
    const opacity = mode === ThemeMode.Dark ? '80%' : '100%';
    return `rgb(${rgbArray[0]} ${rgbArray[1]} ${rgbArray[2]} / ${opacity})`;
  }, [course, mode]);

  const interactiveProps = isClickable
    ? {
        role: 'link',
        tabIndex: 0,
        onClick: () => onClick?.(course.id),
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
          if (event.key === 'Enter') {
            onClick?.(course.id);
          }
        },
      }
    : {};

  return (
    <div
      className={cn('courseTile', {
        dark: mode === ThemeMode.Dark,
        'courseTile--inert': !isClickable,
      })}
      aria-label={t('dashboard:mycourses:navigation_label', {
        defaultValue: 'Navigation zur Unterseite des Kurses: {{coursename}}',
        coursename: course.courseName,
      })}
      style={{
        backgroundColor,
        borderColor,
      }}
      {...interactiveProps}
    >
      {onOpenInBrowser && (
        <div className="courseTileMenu">
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              {(props: MenuTriggerChildProps<'button'>) => (
                <Button
                  {...props}
                  appearance="transparent"
                  icon={<MoreHorizontalRegular />}
                  aria-label={t('dashboard:mycourses:menu_aria_label', {
                    defaultValue: 'Weitere Aktionen',
                  })}
                />
              )}
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<Open24Regular />} onClick={() => onOpenInBrowser(course.id)}>
                  {t('dashboard:mycourses:open_in_browser', {
                    defaultValue: 'In MNSpro im Browser öffnen',
                  })}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      )}
      <div className="flex flex-col gap-2 items-center justify-center">
        <LazyAvatarGroup
          shape="square"
          groupId={get(course, 'azureGroupId') || get(course, 'id', '')}
          name={course.courseName}
          size={48}
        />

        <div className="flex flex-col">
          <Text className="courseName" weight="bold">
            {course.courseName}
          </Text>
          {course.classNames && course.classNames.length > 0 && (
            <Tooltip content={classNames} relationship="label">
              <Text className="courseClassNames">{classNames}</Text>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};
